import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { adminApi } from '../lib/api';

/**
 * admin 현장 업종 분류(KSIC) picker — 고객 현장 폼의 `IndustryPicker`(`apps/web/src/components/sites/IndustryPicker.tsx`)와
 * **같은 규칙**이다: 한 단계씩 내려가는 select 사다리 + 검색, 어느 단계에서 멈춰도 저장되고(서버
 * `assertIndustrySelectable`이 단계를 묻지 않는다) 더 내려갈 수 있으면 세세분류까지 고르도록 안내한다.
 *
 * 재료는 admin 업종 관리 API(`/admin/industries`)다 — 고객 쪽 `/industries`와 갈리는 점 둘:
 * - 「더 내려갈 수 있나」를 **비활성 자식까지 센** `_count.children`으로 본다(그 API가 활성 자식만 세는 칸을 주지
 *   않는다). 자식이 전부 비활성이면 다음 단계 select가 빈 채로 열린다 — 막다른 길은 아니다(그 단계에서 멈춰도 저장).
 * - 검색은 서버가 대분류부터·상한 없이 주므로 **세세분류 먼저 · 30개**로 화면이 자른다(고객 쪽 서버 규칙과 같게).
 * 저장된 값으로 열면 `getIndustry(id).parent`를 따라 올라가 경로를 세운다(KSIC 5단계라 왕복 최대 5번).
 */
export interface AdminIndustryOption {
  id: string;
  code: string;
  name: string;
  level: number;
  hasChildren: boolean;
}

export const ADMIN_INDUSTRY_DEEPER_HINT = '세세분류까지 선택하면 해당되는 법정 서류를 더 정확하게 안내해 드립니다.';

const LEVEL_LABELS: Record<number, string> = { 1: '대분류', 2: '중분류', 3: '소분류', 4: '세분류', 5: '세세분류' };
const SEARCH_LIMIT = 30;
const STALE_MS = 30 * 60 * 1000;

type IndustryRow = { id: string; code: string; name: string; level: number; isActive: boolean; _count: { children: number } };
type IndustryDetail = {
  id: string;
  code: string;
  name: string;
  level: number;
  parent: { id: string } | null;
  children: Array<{ isActive: boolean }>;
};

function toOption(row: IndustryRow): AdminIndustryOption {
  return { id: row.id, code: row.code, name: row.name, level: row.level, hasChildren: row._count.children > 0 };
}

function unwrap<T>(res: { success: boolean; data?: T | null; error?: { message?: string } | null }): T {
  if (!res.success || res.data == null) throw new Error(res.error?.message || '업종을 불러오지 못했습니다.');
  return res.data;
}

async function loadChildren(parentId: string | null): Promise<AdminIndustryOption[]> {
  const rows = unwrap(await adminApi.getIndustries({ parentId: parentId ?? 'null', isActive: true }));
  return rows.map(toOption);
}

async function searchIndustries(query: string): Promise<AdminIndustryOption[]> {
  const rows = unwrap(await adminApi.getIndustries({ search: query, isActive: true }));
  return rows
    .map(toOption)
    .sort((a, b) => b.level - a.level || a.code.localeCompare(b.code))
    .slice(0, SEARCH_LIMIT);
}

async function loadPath(id: string): Promise<AdminIndustryOption[]> {
  const path: AdminIndustryOption[] = [];
  let currentId: string | null = id;
  for (let guard = 0; currentId !== null && guard < 10; guard += 1) {
    const row: IndustryDetail = unwrap(await adminApi.getIndustry(currentId));
    path.unshift({
      id: row.id,
      code: row.code,
      name: row.name,
      level: row.level,
      hasChildren: row.children.some((c) => c.isActive),
    });
    currentId = row.parent?.id ?? null;
  }
  return path;
}

const optionLabel = (o: AdminIndustryOption) => `${o.code} · ${o.name}`;

const CONTROL_CLASS =
  'w-full px-3 py-2 text-sm border border-gray-300 rounded-control focus:ring-2 focus:ring-brand-500 focus:border-transparent';

function LevelSelect({
  parentId,
  depth,
  selectedId,
  disabled,
  onPick,
}: {
  parentId: string | null;
  depth: number;
  selectedId: string;
  disabled?: boolean;
  onPick: (option: AdminIndustryOption | null) => void;
}) {
  const query = useQuery({
    queryKey: ['admin', 'industries', 'children', parentId ?? 'root'],
    queryFn: () => loadChildren(parentId),
    staleTime: STALE_MS,
  });
  const options = query.data ?? [];
  const levelLabel = LEVEL_LABELS[depth + 1] ?? `${depth + 1}단계`;
  return (
    <select
      aria-label={`업종 분류 ${levelLabel}`}
      value={selectedId}
      disabled={disabled || query.isLoading}
      className={CONTROL_CLASS}
      onChange={(e) => onPick(options.find((o) => o.id === e.target.value) ?? null)}
    >
      <option value="">{depth === 0 ? '선택하세요' : `${levelLabel} 선택 (여기서 멈춰도 저장됩니다)`}</option>
      {options.map((o) => (
        <option key={o.id} value={o.id}>
          {optionLabel(o)}
        </option>
      ))}
    </select>
  );
}

export default function AdminIndustryPicker({
  value,
  onChange,
  disabled,
}: {
  /** `Industry.id` — 빈 문자열이면 미지정 */
  value: string;
  onChange: (industryId: string) => void;
  disabled?: boolean;
}) {
  const [path, setPath] = useState<AdminIndustryOption[]>([]);
  const tailId = path.length > 0 ? path[path.length - 1].id : '';
  const needsPath = value !== '' && tailId !== value;

  const pathQuery = useQuery({
    queryKey: ['admin', 'industries', 'path', value],
    queryFn: () => loadPath(value),
    enabled: needsPath,
    staleTime: STALE_MS,
  });
  useEffect(() => {
    if (value === '') {
      if (path.length > 0) setPath([]);
      return;
    }
    if (needsPath && pathQuery.data) setPath(pathQuery.data);
  }, [value, needsPath, pathQuery.data, path.length]);

  const [searchText, setSearchText] = useState('');
  const [debounced, setDebounced] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebounced(searchText.trim()), 250);
    return () => clearTimeout(t);
  }, [searchText]);
  const searchQuery = useQuery({
    queryKey: ['admin', 'industries', 'search', debounced],
    queryFn: () => searchIndustries(debounced),
    enabled: debounced.length > 0,
    staleTime: STALE_MS,
  });

  const pickAt = (depth: number, option: AdminIndustryOption | null) => {
    const next = option ? [...path.slice(0, depth), option] : path.slice(0, depth);
    setPath(next);
    onChange(next.length > 0 ? next[next.length - 1].id : '');
  };

  const pickFromSearch = (option: AdminIndustryOption) => {
    setSearchText('');
    setDebounced('');
    setPath([]); // 경로는 다시 받는다(검색 결과는 조상을 싣지 않는다) — 위 effect가 세운다
    onChange(option.id);
  };

  const tail = path.length > 0 ? path[path.length - 1] : null;
  // 경로를 못 받으면 대분류부터 다시 고를 수 있게 사다리를 연다 — 막다른 길 금지
  const pathFailed = needsPath && pathQuery.isError;
  const waitingPath = needsPath && !pathFailed;
  const showNext = !waitingPath && (tail === null || tail.hasChildren);
  const depths = waitingPath ? [] : [...path.map((_, i) => i), ...(showNext ? [path.length] : [])];
  const results = searchQuery.data ?? [];

  return (
    <div className="space-y-2">
      <div className="grid gap-2 sm:grid-cols-2">
        {depths.map((depth) => (
          <LevelSelect
            key={`${depth}:${depth === 0 ? 'root' : path[depth - 1].id}`}
            parentId={depth === 0 ? null : path[depth - 1].id}
            depth={depth}
            selectedId={path[depth]?.id ?? ''}
            disabled={disabled}
            onPick={(o) => pickAt(depth, o)}
          />
        ))}
        {waitingPath && <p className="text-xs text-gray-500">업종 분류를 불러오는 중…</p>}
        {pathFailed && <p className="text-xs text-gray-500">저장된 업종 분류를 불러오지 못했습니다. 다시 골라 주세요.</p>}
      </div>

      <div>
        <label htmlFor="admin-site-industry-search" className="block text-xs text-gray-500 mb-1">
          업종 분류 검색
        </label>
        <input
          id="admin-site-industry-search"
          type="search"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          disabled={disabled}
          className={CONTROL_CLASS}
          placeholder="예: 금속 가공, C25"
        />
        {debounced.length > 0 && (
          <ul
            className="mt-1 max-h-56 overflow-y-auto rounded-control border border-gray-200 bg-white text-sm"
            role="listbox"
            aria-label="업종 분류 검색 결과"
          >
            {results.length === 0 && !searchQuery.isLoading && (
              <li className="px-3 py-2 text-gray-500">검색 결과가 없습니다.</li>
            )}
            {results.map((o) => (
              <li key={o.id} role="option" aria-selected={o.id === value}>
                <button
                  type="button"
                  className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left hover:bg-gray-50"
                  onClick={() => pickFromSearch(o)}
                >
                  <span className="min-w-0 truncate">{optionLabel(o)}</span>
                  <span className="shrink-0 text-xs text-gray-500">{LEVEL_LABELS[o.level] ?? `${o.level}단계`}</span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {tail && tail.hasChildren && <p className="text-xs text-gray-500">{ADMIN_INDUSTRY_DEEPER_HINT}</p>}
    </div>
  );
}
